import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { ASSETS } from "#/lib/assets";
import { ROUTES } from "#/lib/routes";
import { ArrowBtn, Skeleton } from "../primitives.js";

const SLIDE_TRANSITION =
  "transform 600ms cubic-bezier(0.22, 1, 0.36, 1), opacity 500ms ease-out, filter 500ms ease-out";

const SIDE_OFFSET_PX = 360;

function relativeOffset(idx: number, current: number, total: number): number {
  let off = idx - current;
  if (off > total / 2) off -= total;
  if (off < -total / 2) off += total;
  return off;
}

type Slide = {
  key: string;
  title: string;
  imageUrl: string;
};

// LIFEPAC subject covers (bundled set images), shown in the design's order.
// ASSETS.lifepacSets order is [History & Geography, Math, Language Arts, Science].
const AWARD_WINNING_SLIDES: readonly Slide[] = [
  {
    key: "history-geography",
    title: "History & Geography",
    imageUrl: ASSETS.lifepacSets[0] ?? "",
  },
  {
    key: "language-arts",
    title: "Language Arts",
    imageUrl: ASSETS.lifepacSets[2] ?? "",
  },
  { key: "science", title: "Science", imageUrl: ASSETS.lifepacSets[3] ?? "" },
  { key: "math", title: "Math", imageUrl: ASSETS.lifepacSets[1] ?? "" },
];

export function AwardWinning() {
  const [i, setI] = useState(0);
  const isLoading = false;
  const slides = AWARD_WINNING_SLIDES;
  const total = slides.length;

  if (!isLoading && total === 0) return null;

  return (
    <section className="relative pt-12">
      <div className="rounded-t-[60px] bg-lp-green py-20 text-white">
        <div className="px-6 md:px-20 text-center">
          <h2 className="m-0 mb-8 md:mb-12 font-bold font-display text-3xl md:text-5xl text-white leading-tight">
            AWARD WINNING CURRICULUM
          </h2>
          <div className="mb-6 font-bold font-display text-xl md:text-2xl text-white tracking-wide">
            LIFEPAC HOMESCHOOL CURRICULUM
          </div>
          <p className="mx-auto mb-10 md:mb-14 max-w-3xl whitespace-pre-line font-display text-sm md:text-base text-white leading-relaxed">
            LIFEPAC is a Christian homeschool curriculum created by Alpha Omega
            Publications for grades K-12. Designed by a team of accomplished
            educators with years of classroom experience, LIFEPAC is based on
            the principle of mastery learning.{"\n\n"}
            Ten LIFEPAC worktexts provide one full year of learning content per
            grade level; individual worktexts can be used as supplemental
            material. Mix and match subjects or grade-level worktexts to
            personalize your student's curriculum content.
          </p>
        </div>

        <div className="relative mx-auto h-[420px] w-full max-w-figma">
          {isLoading ? (
            <Skeleton
              className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 max-w-full rounded-2xl bg-white/30 md:rounded-3xl"
              style={{ width: 580, aspectRatio: "580 / 390" }}
            />
          ) : (
            slides.map((slide, idx) => {
              const offset = relativeOffset(idx, i, total);
              const isCenter = offset === 0;
              const visible = Math.abs(offset) <= 1;
              return (
                <Link
                  to={ROUTES.lifepac}
                  key={slide.key}
                  aria-label={slide.title}
                  className="absolute left-1/2 top-1/2 origin-center no-underline"
                  style={{
                    transform: `translate(-50%, -50%) translateX(${offset * SIDE_OFFSET_PX}px) scale(${isCenter ? 1 : 0.7})`,
                    opacity: !visible ? 0 : isCenter ? 1 : 0.7,
                    filter: isCenter ? "none" : "blur(4px)",
                    transition: SLIDE_TRANSITION,
                    pointerEvents: visible ? undefined : "none",
                    zIndex: isCenter ? 2 : 1,
                  }}
                >
                  <div
                    className="flex flex-col items-center justify-center rounded-2xl md:rounded-3xl bg-white p-3 md:p-4"
                    style={{ width: 580, aspectRatio: "580 / 390" }}
                  >
                    <img
                      src={slide.imageUrl}
                      alt={slide.title}
                      loading="lazy"
                      decoding="async"
                      className="max-h-[80%] max-w-full object-contain"
                    />
                    {isCenter && (
                      <div className="mt-2 px-4 text-center font-display font-semibold text-base text-lp-green-dk md:text-lg line-clamp-2">
                        {slide.title}
                      </div>
                    )}
                  </div>
                </Link>
              );
            })
          )}
        </div>

        {!isLoading && (
          <div className="mt-10 flex items-center justify-center gap-6">
            <ArrowBtn
              dir="left"
              onClick={() => setI((x) => (x - 1 + total) % total)}
            />
            <ArrowBtn
              dir="right"
              onClick={() => setI((x) => (x + 1) % total)}
            />
          </div>
        )}
        <div className="mt-10 flex justify-center">
          <Link
            to={ROUTES.shop}
            search={{bundles: ["lifepac"]}}
            className="inline-flex h-[60px] cursor-pointer items-center justify-center rounded-full border-0 bg-white px-12 font-display font-semibold text-lg text-lp-green-dk hover:bg-lp-green-soft"
          >
            Shop Now
          </Link>
        </div>
      </div>
    </section>
  );
}
